import React from 'react'
import api from 'api/api'
import { Answer, IReaction } from 'types/answers'
import { SignalType } from 'types/signals'
import { DEFAULT_UPVALUE_EMOJI } from 'components/votes/upvalueEmoji'
import { recordRecentReactionEmoji } from 'components/votes/recentReactions'
import UpvalueConfirmMessage, { UpvalueConfirmAction } from 'components/votes/UpvalueConfirmMessage'
import TopUpWalletAddress from 'components/votes/TopUpWalletAddress'
import { paidSignalActionLabel } from 'components/votes/upvalueConfirmCopy'
import {
    BUY_BSV_MODAL_KEY,
    FREE_UPVALUE_INTRO_MODAL_KEY,
    PAID_UPVALUE_INTRO_MODAL_KEY,
    SEED_INTRO_MODAL_KEY,
} from 'components/votes/upvalueModalKeys'
import useStore, { PendingUpvalueOp } from 'state/knovStore'
import { updateCachedAnswer, getCachedAnswer } from 'state/cache'
import { waitForWalletBalance, getCachedWalletDetails } from 'lib/walletBalance'
import { seedMinimumDefaultOptions } from 'lib/paidSignalDefaults'
import { BsvWalletType } from 'wallets/wallets'
import { getClientShare } from 'lib/bsv-signing'
import { offerWalletPasskeyRestore } from 'lib/walletPasskeyRestoreOffer'
import {
    answerWithActiveSignalSettings,
    canSendFreeReactionForAnswerSnapshot,
    currentUserReactionForAnswer,
    nextPressIsFreeReactionForAnswer,
    onlyPaidUpvalueEnabled,
    sameUserId,
} from 'state/freeReactionEligibility'
import {
    shouldInterceptPaidUpvalueIntro,
    shouldInterceptSeedIntro,
    shouldShowFreeUpvalueIntro,
} from 'state/upvalueIntroVisibility'

import queryClient from 'api/queryClient'
import { WALLET_DETAILS_QUERY_KEY, WalletDetails } from 'api/useWalletDetails'
import { getMediaDockSnapshot } from 'state/mediaPlaybackCoordinator'
import { reportUpvalueFailure } from 'utils/upvalueFailureReports'
import { treechatSignsWithoutLocalKey } from 'lib/clientSigningFeature'
import { nowPlayingBroadcastEnabled } from 'state/nowPlayingBroadcast'
import { treechatRadioEnabled } from 'lib/treechatRadio'
import { createUpvalueBalanceToast, upvalueBalanceToastEnabled } from 'state/upvalueBalanceToast'
import { formatSatsForBsvUnit, satsDisplayUnit } from 'components/shared/useBsvDisplay'
import { USD_UNIT } from 'lib/bsv-util'

const LOCK_REQUEST_BATCH_WINDOW_MS = 1000
const FULL_UPVALUE_MANIFEST_FEATURE = 'use_full_upvalue_manifest'
const pendingTimers: Record<string, number | null> = {}
const retryAttemptsByCohort: Record<string, number> = {}
const MAX_RETRY_ATTEMPTS = 6
const RETRY_BACKOFF_MS = [300, 800, 1500, 2500, 4000, 6000]
const ORDINAL_OWNER_REFRESH_MAX_RETRY_ATTEMPTS = 10
const ORDINAL_OWNER_REFRESH_RETRY_BACKOFF_MS = [
    300, 800, 1500, 2500, 4000, 6000, 6000, 6000, 6000, 6000,
]

const cachedBsvExchangeRate = () =>
    queryClient.getQueryData<{ exchangerate?: number }>(['bsv-exchange-rate'])?.exchangerate ?? null
const scheduleUpvalueBalanceToast = createUpvalueBalanceToast({
    isEnabled: () => upvalueBalanceToastEnabled(gon?.currentUser?.space_options),
    readBalance: () => {
        const balance = queryClient.getQueryData<WalletDetails>([
            WALLET_DETAILS_QUERY_KEY,
            useStore.getState().currentUserBsvWalletType || BsvWalletType.SHUALLET,
        ])?.balance
        return typeof balance === 'number' ? balance : null
    },
    formatBalance: sats =>
        formatSatsForBsvUnit(sats, useStore.getState().bsvUnit, cachedBsvExchangeRate()),
    noticeVariant: sats =>
        satsDisplayUnit(sats, useStore.getState().bsvUnit, cachedBsvExchangeRate()) === USD_UNIT
            ? 'usd'
            : 'bitcoin',
    currentNotice: () => useStore.getState().globalNotice,
    showNotice: (message, options) =>
        useStore.getState().showGlobalNotice(message, {
            testId: 'upvalue-balance-toast',
            ...options,
        }),
    updateNotice: (kind, message, options) =>
        useStore.getState().updateGlobalNotice(kind, message, options),
})

type UpvalueQueueError = Error & {
    statusCode?: number
    apiStatus?: string
    retryable?: boolean
    requiresWalletSetup?: boolean
}

export type ReactOrUpvalueOptions = {
    isSeed?: boolean
    seedIntroAcknowledged?: boolean
    reactionEmoji?: string
    sentimentSource?: string
    source?: string
    // Artifact-surface presses (ordinals stream dock) carry the ordinal id so
    // the payout routes to the ordinal's current owner.
    artifactOrdinalId?: string
    onFreeReactionCommitted?: (reaction: IReaction) => void
    onFreeReactionFailed?: (error: unknown) => void
}

const userHasFeature = (featureName: string) => {
    const features = gon?.currentUser?.features || []
    return features.some(
        feature => (typeof feature === 'string' ? feature : feature?.name) === featureName,
    )
}

const isModalOpenWithKey = (modalKey: string) => {
    const store = useStore.getState()
    return store.isModalOpen && store.modalParams?.modalKey === modalKey
}

const generatePendingId = () => {
    const globalCrypto = typeof crypto !== 'undefined' ? crypto : undefined
    if (globalCrypto?.randomUUID) {
        return globalCrypto.randomUUID()
    }

    const bytes = new Uint8Array(16)
    if (globalCrypto?.getRandomValues) {
        globalCrypto.getRandomValues(bytes)
    } else {
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Math.floor(Math.random() * 256)
        }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80

    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
        16,
        20,
    )}-${hex.slice(20)}`
}

const createNonRetryableUpvalueError = (
    message: string,
    options?: { requiresWalletSetup?: boolean },
) => {
    const error = new Error(message) as UpvalueQueueError
    error.retryable = false
    error.requiresWalletSetup = options?.requiresWalletSetup || false
    return error
}

const scheduleProcessing = (answerId: string, delayMs: number = LOCK_REQUEST_BATCH_WINDOW_MS) => {
    if (typeof window === 'undefined') {
        return
    }
    if (pendingTimers[answerId]) {
        window.clearTimeout(pendingTimers[answerId])
    }
    pendingTimers[answerId] = window.setTimeout(() => {
        pendingTimers[answerId] = null
        processAnswerQueue(answerId).catch(err => {
            console.error('Error processing upvalue queue', err)
            reportUpvalueFailure('scheduler_crashed', {
                answer_id: answerId,
                error: (err as Error)?.message,
            })
        })
    }, delayMs)
}

const cohortKeyForOp = (answerId: string, op: PendingUpvalueOp) =>
    `${answerId}::${op.isBoost ? 'boost' : op.isSeed ? 'seed' : 'upvalue'}::${
        op.reactionEmoji || ''
    }::${op.observedAnswerSnapshotSha256 || ''}::${op.artifactOrdinalId || ''}::${
        op.nowPlayingAnswerId || ''
    }`

// Dynamic playlist: the audio answer the dock is playing right now, captured
// at press time so the upvalue can credit the song ("@user is playing …").
// Only audio tracks can air on the station, so only they are captured.
const captureNowPlayingAnswerId = (): string | undefined => {
    try {
        // Either feature wants the now-playing context: the station credits
        // it, the announcement card broadcasts it.
        if (!treechatRadioEnabled() && !userHasFeature('media_announcement')) {
            return undefined
        }
        if (!nowPlayingBroadcastEnabled()) return undefined

        const snapshot = getMediaDockSnapshot()
        const track = snapshot?.nowPlayingTrack
        // Songs are audio or native video; YouTube never airs on the station.
        const isSong = track?.kind === 'audio' || track?.kind === 'video'
        if (!snapshot?.isPlaying || !isSong || !track?.answerId) return undefined
        return String(track.answerId)
    } catch {
        return undefined
    }
}

const takeLeadingCohortBatch = (answerId: string, queue: PendingUpvalueOp[]) => {
    if (!queue.length) return []
    if (queue[0].isSeed) return [queue[0]]

    const firstCohortKey = cohortKeyForOp(answerId, queue[0])
    const batch: PendingUpvalueOp[] = []

    for (const op of queue) {
        if (cohortKeyForOp(answerId, op) !== firstCohortKey) {
            break
        }
        batch.push(op)
    }

    return batch
}

const attachSyntheticUpvalue = (answerId: string, op: PendingUpvalueOp) => {
    const cachedAnswer = getCachedAnswer(answerId, new Set())
    const baseAnswer = cachedAnswer || ({} as Answer)
    const existingUpvalues = baseAnswer?.bsv_upvalues || []
    const newAmount = (baseAnswer?.bsv_upvalue_amt || 0) + op.sats

    const syntheticUpvalue = {
        id: `synthetic-${op.id}`,
        answer_id: answerId,
        from_user_id: gon.currentUser?.id,
        to_user_id: baseAnswer?.user_id,
        tx_id: null,
        amount: op.sats.toString(),
        created_at: op.createdAt,
        updated_at: op.createdAt,
        boost: false,
        is_boost: op.isBoost,
        is_seed: op.isSeed,
        metadata: op.reactionEmoji
            ? {
                  sentiment_emoji: op.reactionEmoji,
                  sentiment_source: op.sentimentSource || 'react_button',
              }
            : {},
        is_synthetic: true,
        pending_queue_id: op.id,
    }

    updateCachedAnswer(answerId, {
        bsv_upvalue_amt: newAmount,
        bsv_upvalues: [...existingUpvalues, syntheticUpvalue],
    })
}

const rollbackSyntheticUpvalues = (answerId: string, opIds: string[]) => {
    const cachedAnswer = getCachedAnswer(answerId, new Set())
    if (!cachedAnswer) return

    const upvalues = cachedAnswer.bsv_upvalues || []
    const removed = upvalues.filter(upvalue => opIds.includes(upvalue?.pending_queue_id))
    if (removed.length === 0) return

    const remaining = upvalues.filter(upvalue => !opIds.includes(upvalue?.pending_queue_id))
    const removedTotal = removed.reduce((sum, upvalue) => sum + Number(upvalue?.amount || 0), 0)

    updateCachedAnswer(answerId, {
        bsv_upvalues: remaining,
        bsv_upvalue_amt: Math.max(0, (cachedAnswer?.bsv_upvalue_amt || 0) - removedTotal),
    })
}

const markSyntheticUpvaluesSubmitted = (
    answerId: string,
    opIds: string[],
    coalescedUpvalueId: string,
) => {
    const cachedAnswer = getCachedAnswer(answerId, new Set())
    if (!cachedAnswer?.bsv_upvalues?.length) return

    let didUpdate = false
    const nextUpvalues = cachedAnswer.bsv_upvalues.map(upvalue => {
        if (!opIds.includes(upvalue?.pending_queue_id)) return upvalue
        if (upvalue.coalesced_upvalue_id === coalescedUpvalueId) return upvalue

        didUpdate = true
        return {
            ...upvalue,
            coalesced_upvalue_id: coalescedUpvalueId,
        }
    })

    if (didUpdate) {
        updateCachedAnswer(answerId, { bsv_upvalues: nextUpvalues })
    }
}

const isRetryableUpvalueError = (error: unknown) => {
    const err = error as UpvalueQueueError
    if (err?.retryable === false) return false
    if (err?.apiStatus === 'stale_answer') return false
    if (err?.retryable) return true
    if (err?.apiStatus === 'answer_not_ready' || err?.apiStatus === 'not_found') return true
    if (err?.statusCode === 404 || err?.statusCode === 429) return true
    if (typeof err?.statusCode === 'number' && err.statusCode >= 500) return true
    const message = String(err?.message || '').toLowerCase()
    if (message.includes('failed to fetch') || message.includes('networkerror')) return true
    return false
}

const processAnswerQueue = async (answerId: string) => {
    const store = useStore.getState()
    const pending = store.pendingUpvaluesByAnswer[answerId]
    if (!pending || pending.length === 0) {
        pendingTimers[answerId] = null
        return
    }

    if (store.upvalueQueueStatus[answerId] === 'processing') {
        pendingTimers[answerId] = null
        return
    }

    store.setUpvalueQueueStatus(answerId, 'processing')

    let retryDelayMs: number | null = null
    let batch: PendingUpvalueOp[] = []
    let cohortKey = ''
    try {
        const walletType = store.currentUserBsvWalletType || BsvWalletType.SHUALLET
        let walletDetails = getCachedWalletDetails(walletType)
        let availableBalance = walletDetails?.balance || 0

        if (!walletDetails?.balanceLoaded) {
            const resolvedBalance = await waitForWalletBalance(walletType)
            if (resolvedBalance === null) {
                throw new Error('Unable to verify wallet balance. Please try again.')
            }
            availableBalance = resolvedBalance
        }

        const queue = [
            ...(useStore.getState().pendingUpvaluesByAnswer[answerId] || []),
        ] as PendingUpvalueOp[]
        if (queue.length === 0) {
            return
        }
        const firstOp = queue[0]
        cohortKey = cohortKeyForOp(answerId, firstOp)
        batch = takeLeadingCohortBatch(answerId, queue)
        if (batch.length === 0) return

        const totalSats = batch.reduce((sum, op) => sum + op.sats, 0)
        if (availableBalance < totalSats) {
            openBuyBsvModal()
            throw new Error('Not enough coins to upvalue. Please deposit more coins.')
        }

        const containsBoost = batch[0]?.isBoost || false
        const containsSeed = batch[0]?.isSeed || false
        const reactionEmoji = batch[0]?.reactionEmoji
        const coalescedUpvalueId = firstOp.id
        const batchIds = batch.map(op => op.id)
        markSyntheticUpvaluesSubmitted(answerId, batchIds, coalescedUpvalueId)

        // Only send device state when the feature flag is enabled for this user.
        let deviceId: string | undefined
        const userId = gon.currentUser?.id
        const hasClientSigning = userHasFeature('use_client_signing')
        if (userId && hasClientSigning) {
            const clientShare = await getClientShare(userId)
            if (!clientShare?.deviceId && treechatSignsWithoutLocalKey()) {
                // No device id: Treechat signs this one (Promotion.device_signing?).
            } else if (!clientShare?.deviceId) {
                // The unlock screen or the one-tap passkey notice restores this browser;
                // otherwise send the user to the wallet modal to restore or set up.
                const restoreOffered = await offerWalletPasskeyRestore({ fromAction: true }).catch(
                    () => false,
                )
                if (!restoreOffered) store.actions?.modalActions?.openWalletModal?.()
                throw createNonRetryableUpvalueError(
                    restoreOffered
                        ? 'This browser lost its wallet keys. Restore them, then upvalue again.'
                        : 'Self-custody signing is enabled, but this device does not have a local key set up. Restore a wallet backup or set up this device in the wallet modal before sending upvalues.',
                    { requiresWalletSetup: !restoreOffered },
                )
            } else {
                deviceId = clientShare.deviceId
            }
        }

        await api.sendUpvalue(
            answerId,
            totalSats,
            containsBoost,
            coalescedUpvalueId,
            reactionEmoji,
            deviceId,
            firstOp.observedAnswerSnapshotSha256,
            containsSeed,
            firstOp.artifactOrdinalId,
            firstOp.nowPlayingAnswerId,
        )
        delete retryAttemptsByCohort[cohortKey]

        // Optimistically debit the wallet balance
        queryClient.setQueryData(
            [WALLET_DETAILS_QUERY_KEY, walletType],
            (oldData: WalletDetails | undefined) => {
                if (!oldData) return oldData
                return {
                    ...oldData,
                    balance: oldData.balance - totalSats,
                    confirmedBalance: (oldData.confirmedBalance || 0) - totalSats,
                }
            },
        )
        scheduleUpvalueBalanceToast()

        store.clearPendingUpvalues(answerId, batchIds)
    } catch (error) {
        const errMessage = (error as UpvalueQueueError)?.message || 'Error upvaluing coins.'
        const retryable = isRetryableUpvalueError(error)
        if (retryable) {
            const nextAttempt = (retryAttemptsByCohort[cohortKey] || 0) + 1
            const waitingForOrdinalOwner =
                (error as UpvalueQueueError)?.apiStatus === 'ordinal_owner_refreshing'
            const maxAttempts = waitingForOrdinalOwner
                ? ORDINAL_OWNER_REFRESH_MAX_RETRY_ATTEMPTS
                : MAX_RETRY_ATTEMPTS
            const backoff = waitingForOrdinalOwner
                ? ORDINAL_OWNER_REFRESH_RETRY_BACKOFF_MS
                : RETRY_BACKOFF_MS
            if (nextAttempt <= maxAttempts) {
                retryAttemptsByCohort[cohortKey] = nextAttempt
                retryDelayMs = backoff[Math.min(nextAttempt - 1, backoff.length - 1)]
                console.warn('Retrying upvalue request while its dependency becomes ready', {
                    answerId,
                    cohortKey,
                    attempt: nextAttempt,
                    retryDelayMs,
                    error: errMessage,
                })
            }
        }

        if (retryDelayMs == null) {
            const attempts = retryAttemptsByCohort[cohortKey]
            delete retryAttemptsByCohort[cohortKey]
            console.error('Error processing upvalues:', error)
            const failedWallet = getCachedWalletDetails(
                useStore.getState().currentUserBsvWalletType || BsvWalletType.SHUALLET,
            )
            reportUpvalueFailure('queue_failed', {
                answer_id: answerId,
                sats: batch.reduce((sum, op) => sum + op.sats, 0) || undefined,
                error: errMessage,
                api_status: (error as UpvalueQueueError)?.apiStatus,
                status_code: (error as UpvalueQueueError)?.statusCode,
                retryable,
                attempts,
                requires_wallet_setup: (error as UpvalueQueueError)?.requiresWalletSetup,
                client_signing: userHasFeature('use_client_signing'),
                balance_loaded: failedWallet?.balanceLoaded === true,
                balance: failedWallet?.balance,
            })
            const rollbackIds =
                batch.length > 0
                    ? batch.map(op => op.id)
                    : (useStore.getState().pendingUpvaluesByAnswer[answerId] || []).map(op => op.id)
            if (rollbackIds?.length) {
                rollbackSyntheticUpvalues(answerId, rollbackIds)
                useStore.getState().clearPendingUpvalues(answerId, rollbackIds)
            }
            const minSeedSats = (error as UpvalueQueueError & { minSeedSats?: number })?.minSeedSats
            if (
                (error as UpvalueQueueError)?.apiStatus === 'seed_below_minimum' &&
                typeof minSeedSats === 'number'
            ) {
                useStore.setState(state => seedMinimumDefaultOptions(state.seedSats, minSeedSats))
            }
            if (userHasFeature('alert_user_upvalue_failure')) {
                alert(errMessage)
            } else if ((error as UpvalueQueueError)?.requiresWalletSetup) {
                alert(errMessage)
            }
        }
    } finally {
        useStore.getState().setUpvalueQueueStatus(answerId, 'idle')
        pendingTimers[answerId] = null
        const remaining = useStore.getState().pendingUpvaluesByAnswer[answerId]
        if (remaining?.length) {
            if (retryDelayMs != null) scheduleProcessing(answerId, retryDelayMs)
            else scheduleProcessing(answerId, remaining[0]?.isSeed ? 0 : undefined)
        }
    }
}

const freshAnswer = (answer: Answer): Answer => {
    const cachedAnswer = getCachedAnswer(answer.id, new Set())
    if (!cachedAnswer) return answer

    return answerWithActiveSignalSettings(cachedAnswer, answer)
}

const signalQuestIdForAnswer = (answer: Answer): string | null | undefined => {
    const cached = freshAnswer(answer)
    return cached.signal_quest_id || cached.quest_id || cached.downtree_quest_id
}

const currentUserReaction = (answer: Answer): IReaction | undefined => {
    return currentUserReactionForAnswer(answer, gon.currentUser?.id)
}

// Free-first stays isolated behind the product gate. True when the gate and
// answer state make the next press a free reaction.
export const nextPressIsFreeReaction = (answer: Answer): boolean => {
    if (onlyPaidUpvalueEnabled()) return false

    return nextPressIsFreeReactionForAnswer(freshAnswer(answer), gon.currentUser?.id)
}

export const upvaluesEnabledForAnswer = (answer: Answer): boolean =>
    freshAnswer(answer).upvalues_enabled !== false

export const canSendFreeReactionForAnswer = (answer: Answer): boolean => {
    if (onlyPaidUpvalueEnabled()) return false

    return canSendFreeReactionForAnswerSnapshot(freshAnswer(answer), gon.currentUser?.id)
}

const setCachedReactions = (answerId: string, reactions: IReaction[]) => {
    updateCachedAnswer(answerId, { reactions })
}

export const submitFreeReaction = (
    answer: Answer,
    emoji: string = DEFAULT_UPVALUE_EMOJI,
    options?: Pick<ReactOrUpvalueOptions, 'onFreeReactionCommitted' | 'onFreeReactionFailed'>,
) => {
    const currentUserId = gon.currentUser?.id
    if (currentUserId == null) return

    const cached = freshAnswer(answer)
    const previousReactions = cached.reactions || []
    const previousUserReaction = currentUserReaction(cached)
    const otherReactions = previousReactions.filter(
        reaction => !sameUserId(reaction.user_id, currentUserId),
    )

    window.logEv('FREE_REACTION', {
        answer_id: answer.id,
        quest_id: signalQuestIdForAnswer(answer),
        target_user_id: answer.user_id,
        reaction_emoji: emoji,
    })
    recordRecentReactionEmoji(emoji)

    const syntheticReaction: IReaction = {
        id: `synthetic-reaction-${answer.id}-${currentUserId}`,
        answer_id: answer.id,
        user_id: currentUserId,
        emoji,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_name: gon.currentUser?.name,
        is_synthetic: true,
    }
    setCachedReactions(answer.id, [...otherReactions, syntheticReaction])

    api.sendReaction(answer.id, emoji, signalQuestIdForAnswer(answer))
        .then(data => {
            if (data?.reaction) {
                const latest = freshAnswer(answer)
                const latestUserReaction = currentUserReaction(latest)
                if (!latestUserReaction || latestUserReaction.id === syntheticReaction.id) {
                    const latestOtherReactions = (latest.reactions || []).filter(
                        reaction => !sameUserId(reaction.user_id, currentUserId),
                    )
                    setCachedReactions(answer.id, [...latestOtherReactions, data.reaction])
                }
                options?.onFreeReactionCommitted?.(data.reaction)
            }
        })
        .catch(error => {
            console.error('Error sending free reaction:', error)
            const latest = freshAnswer(answer)
            const latestUserReaction = currentUserReaction(latest)
            if (!latestUserReaction || latestUserReaction.id === syntheticReaction.id) {
                const latestOtherReactions = (latest.reactions || []).filter(
                    reaction => !sameUserId(reaction.user_id, currentUserId),
                )
                setCachedReactions(
                    answer.id,
                    previousUserReaction
                        ? [...latestOtherReactions, previousUserReaction]
                        : latestOtherReactions,
                )
            }
            options?.onFreeReactionFailed?.(error)
            const statusCode = (error as UpvalueQueueError)?.statusCode
            const message =
                statusCode === 401
                    ? 'Your session expired. Please sign in again to send reactions.'
                    : (error as Error)?.message || 'Failed to send reaction.'
            window.alert?.(message)
        })
}

export const removeFreeReaction = (answer: Answer) => {
    const currentUserId = gon.currentUser?.id
    if (currentUserId == null) return

    const cached = freshAnswer(answer)
    const previousReactions = cached.reactions || []
    const removedUserReaction = currentUserReaction(cached)
    const remaining = previousReactions.filter(
        reaction => !sameUserId(reaction.user_id, currentUserId),
    )
    if (remaining.length === previousReactions.length) return

    setCachedReactions(answer.id, remaining)

    api.removeReaction(answer.id).catch(error => {
        console.error('Error removing free reaction:', error)
        const latest = freshAnswer(answer)
        if (currentUserReaction(latest)) return

        const latestOtherReactions = (latest.reactions || []).filter(
            reaction => !sameUserId(reaction.user_id, currentUserId),
        )
        setCachedReactions(
            answer.id,
            removedUserReaction
                ? [...latestOtherReactions, removedUserReaction]
                : latestOtherReactions,
        )
    })
}

const sendOrToggleFreeReaction = (
    answer: Answer,
    emoji: string,
    options?: Pick<ReactOrUpvalueOptions, 'onFreeReactionCommitted' | 'onFreeReactionFailed'>,
) => {
    if (!canSendFreeReactionForAnswer(answer)) return

    const cached = freshAnswer(answer)
    const existingReaction = currentUserReaction(cached)
    if (existingReaction && cached.upvalues_enabled === false && existingReaction.emoji === emoji) {
        removeFreeReaction(answer)
        return
    }

    submitFreeReaction(answer, emoji, options)
}

// First-time explainer shown when a free-first user lands a free upvote.
// Persisted per-user via the `options` JSONB so it shows exactly once across
// all of the user's devices.
const maybeShowFreeUpvalueIntro = () => {
    const currentUser = gon?.currentUser

    // The intro modal persists `has_seen_upvalue_intro` itself, but only when the
    // user ticks "Don't show this again" — so it keeps reappearing until then.
    if (
        !shouldShowFreeUpvalueIntro(currentUser, isModalOpenWithKey(FREE_UPVALUE_INTRO_MODAL_KEY))
    ) {
        return
    }

    useStore.setState({
        isModalOpen: true,
        modalParams: {
            type: 'FreeUpvalueIntro',
            modalKey: FREE_UPVALUE_INTRO_MODAL_KEY,
            noHeader: true,
            disableOverlayBlur: true,
            customStyles: {
                width: 'min(92dvw, 460px)',
                maxWidth: '460px',
            },
        },
    })
}

// Remember each paid signal intro independently so seeing Upvalue does not hide Seed.
// Persisted per-user via the `options` JSONB.
export const persistPaidSignalIntroSeen = (isSeed: boolean) => {
    try {
        const currentUser = gon?.currentUser
        const seenOption = isSeed ? 'has_seen_seed_intro' : 'has_seen_paid_upvalue_intro'
        if (!currentUser || currentUser.options?.[seenOption]) return

        currentUser.options = { ...(currentUser.options || {}), [seenOption]: true }
        api.updateUserOptions({ [seenOption]: true })?.catch((error: unknown) => {
            if (gon?.currentUser?.options) {
                gon.currentUser.options[seenOption] = false
            }
            console.error('Failed to persist paid signal intro flag', error)
        })
    } catch (error) {
        console.error('Failed to persist paid signal intro flag', error)
    }
}

// The first time a user sends a paid upvalue, confirm before spending. Returns
// true when it intercepted the press (caller should NOT enqueue); the enqueue
// happens from the confirm callback instead.
const maybeConfirmFirstUpvalue = (
    answer: Answer,
    sats: number,
    type: SignalType,
    options?: ReactOrUpvalueOptions,
): boolean => {
    const currentUser = gon?.currentUser
    const isSeed = options?.isSeed === true
    const modalKey = isSeed ? SEED_INTRO_MODAL_KEY : PAID_UPVALUE_INTRO_MODAL_KEY
    let dontShowAgain = true

    if (isSeed && options?.seedIntroAcknowledged) {
        return false
    }

    const shouldIntercept = isSeed
        ? shouldInterceptSeedIntro(currentUser)
        : shouldInterceptPaidUpvalueIntro(currentUser)
    if (!shouldIntercept) {
        return false
    }

    if (isModalOpenWithKey(modalKey)) {
        // The press is dropped while the confirm counts as open; one that never shows
        // (or never closes) would drop every upvalue.
        reportUpvalueFailure('confirm_already_open', { answer_id: answer.id, sats, seed: isSeed })
        return true
    }

    const actionLabel = isSeed ? 'Seed' : paidSignalActionLabel(type)
    reportUpvalueFailure('confirm_shown', { answer_id: answer.id, sats, seed: isSeed })

    useStore.getState().actions?.modalActions?.openConfirmModal?.(
        React.createElement(UpvalueConfirmMessage, {
            signalType: type,
            isSeed,
        }),
        actionLabel,
        () => {
            if (dontShowAgain) {
                persistPaidSignalIntroSeen(isSeed)
            }
            enqueueUpvalueAfterPaidConfirm(answer, sats, type, options)
        },
        {
            // Keep the opt-out checkbox and spend CTA together at the bottom.
            headerTitle: actionLabel,
            modalKey,
            iconType: 'upvalue',
            confirmVariant: 'bsv',
            hideCancel: true,
            disableOverlayBlur: true,
            inlineConfirmActions: true,
            actionPrefix: React.createElement(UpvalueConfirmAction, {
                isSeed,
                onDontShowAgainChange: (value: boolean) => {
                    dontShowAgain = value
                },
            }),
        },
    )
    return true
}

const upvalueWalletType = (): BsvWalletType =>
    useStore.getState().currentUserBsvWalletType || BsvWalletType.SHUALLET

// True when the cached wallet balance is known and covers `sats`. When the
// balance has not loaded yet we optimistically return true and let the
// processing queue verify the balance before sending.
const hasCachedBalanceForUpvalue = (sats: number): boolean => {
    const details = getCachedWalletDetails(upvalueWalletType())
    if (!details?.balanceLoaded) return true
    return (details.balance || 0) >= sats
}

// Minimal "you're out of coins" modal that exposes the wallet's funding address.
export const openBuyBsvModal = () => {
    if (isModalOpenWithKey(BUY_BSV_MODAL_KEY)) {
        return
    }

    const modalActions = useStore.getState().actions?.modalActions
    modalActions?.openConfirmModal?.('You need Bitcoin SV to upvalue! Top up now!', '', () => {}, {
        // Dismissal is handled by the modal shell's top-right close button.
        hideCancel: true,
        hideConfirm: true,
        headerTitle: 'Top up',
        modalKey: BUY_BSV_MODAL_KEY,
        iconType: 'bitcoin',
        confirmVariant: 'bsv',
        disableOverlayBlur: true,
        footerContent: React.createElement(TopUpWalletAddress),
    })
}

function enqueueUpvalueAfterPaidConfirm(
    answer: Answer,
    sats: number,
    type: SignalType,
    options?: ReactOrUpvalueOptions,
) {
    if (!hasCachedBalanceForUpvalue(sats)) {
        reportUpvalueFailure('no_balance_blocked', {
            answer_id: answer.id,
            sats,
            balance: getCachedWalletDetails(upvalueWalletType())?.balance,
        })
        openBuyBsvModal()
        return
    }
    enqueueUpvalue(answer, sats, type, options)
}

// Enqueue a paid upvalue, but short-circuit to the buy-bitcoin modal when the
// known balance can't cover it so the user isn't shown an optimistic upvalue
// that immediately rolls back.
const enqueueUpvalueIfFunded = (
    answer: Answer,
    sats: number,
    type: SignalType,
    options?: ReactOrUpvalueOptions,
) => {
    // First paid upvalue: confirm before spending. This also covers paid
    // reaction-menu sends, which use the shared upvalue/boost confirmation.
    if (maybeConfirmFirstUpvalue(answer, sats, type, options)) {
        return
    }
    enqueueUpvalueAfterPaidConfirm(answer, sats, type, options)
}

// Single entry point for a react-button press. Paid-first users upvalue
// immediately; free-first users land one free reaction before escalating.
export const reactOrUpvalue = (
    answer: Answer,
    sats: number,
    type: SignalType,
    options?: ReactOrUpvalueOptions,
) => {
    const emoji = options?.reactionEmoji || DEFAULT_UPVALUE_EMOJI

    // The signal options menu arms its own value: 0 sats = free reaction,
    // >=1 = paid upvalue. The armed amount is authoritative, so we don't apply
    // the implicit free-first rule here — the menu already surfaces the choice.
    const fromSignalOptions = options?.source === 'signal_options'

    if (fromSignalOptions) {
        if (sats >= 1 && upvaluesEnabledForAnswer(answer)) {
            enqueueUpvalueIfFunded(answer, sats, type, options)
        } else if (sats < 1) {
            sendOrToggleFreeReaction(answer, emoji, options)
        }
        return
    }

    // The explicit free-first preference gets one free quick press.
    if (nextPressIsFreeReaction(answer)) {
        submitFreeReaction(answer, emoji, options)
        maybeShowFreeUpvalueIntro()
        return
    }

    if (upvaluesEnabledForAnswer(answer)) {
        enqueueUpvalueIfFunded(answer, sats, type, options)
        return
    }

    // Upvalues disabled for this stream: repeat presses adjust the free reaction.
    sendOrToggleFreeReaction(answer, emoji, options)
}

export const enqueueUpvalue = (
    answer: Answer,
    sats: number,
    type: SignalType,
    options?: ReactOrUpvalueOptions,
) => {
    const source = options?.source || options?.sentimentSource || 'quick_button'
    const nowPlayingAnswerId = captureNowPlayingAnswerId()
    window.logEv('BSV_UPVALUE', {
        answer_id: answer.id,
        quest_id: answer.quest_id,
        target_user_id: answer.user_id,
        amount_sats: sats,
        signal_type: type,
        reaction_emoji: options?.reactionEmoji,
        source,
        now_playing_answer_id: nowPlayingAnswerId,
    })
    recordRecentReactionEmoji(options?.reactionEmoji)

    const op: PendingUpvalueOp = {
        id: generatePendingId(),
        sats,
        isBoost: type === 'boost',
        isSeed: options?.isSeed === true,
        reactionEmoji: options?.reactionEmoji,
        sentimentSource: options?.sentimentSource,
        artifactOrdinalId: options?.artifactOrdinalId,
        nowPlayingAnswerId,
        createdAt: new Date().toISOString(),
        observedAnswerSnapshotSha256: userHasFeature(FULL_UPVALUE_MANIFEST_FEATURE)
            ? freshAnswer(answer).answer_snapshot_sha256 || undefined
            : undefined,
    }

    useStore.getState().enqueuePendingUpvalue(answer.id, op)
    attachSyntheticUpvalue(answer.id, op)
    scheduleProcessing(answer.id, op.isSeed ? 0 : undefined)

    return op.id
}
